import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { integrationsEndpoints } from '@/api/endpoints';
import { callGet, callPost } from '@/api/fetcher';
import { useDeskId } from '@/hooks/useDeskId';
import { selectAccountSlug } from '@/redux/session/selectors';
import { WHATSAPP_CONFLICT_CODES } from '@/shared/constants/whatsapp';
import { WhatsAppIntegration } from '@/shared/types/integration';
import type { WhatsappValidationResult } from '@/shared/types/whatsapp';
import {
  initializeFacebookSdk,
  OAuthAction,
  performWhatsappOAuth,
  WhatsappConflict,
} from '@/util/facebookSdk';

import { useIntegrations } from './useIntegrations';

interface WhatsappData {
  verified_name?: string;
  code_verification_status?: string;
  display_phone_number?: string;
  quality_rating?: string;
  id?: string;
  is_on_biz_app?: boolean;
  platform_type?: string;
  profile_picture_url?: string;
  name_status?: string;
  throughput?: { level: string };
  whatsapp_business_manager_messaging_limit?: string;
  health_issues?: WhatsappValidationResult[];
}

interface OAuthState {
  phoneNumberId?: string;
  wabaId?: string;
  oauthCode?: string;
  oauthError?: string;
  oauthEvent?: string;
  isConnecting: boolean;
  conflict?: WhatsappConflict;
}

const oauthInitialState: OAuthState = {
  phoneNumberId: undefined,
  wabaId: undefined,
  oauthCode: undefined,
  oauthError: undefined,
  oauthEvent: undefined,
  isConnecting: false,
  conflict: undefined,
};

const oauthReducer = (state: OAuthState, action: OAuthAction): OAuthState => {
  switch (action.type) {
    case 'SET_SESSION_INFO':
      // A preflight conflict is terminal for the round — keep it sticky so a later Finish event
      // can't overwrite it and re-fire completion.
      if (state.conflict) {
        return state;
      }
      return {
        ...state,
        phoneNumberId: action.phoneNumberId,
        wabaId: action.wabaId,
        oauthEvent: action.oauthEvent,
      };
    case 'SET_OAUTH_CODE':
      return { ...state, oauthCode: action.code };
    case 'SET_OAUTH_ERROR':
      // Meta accepts a code once, so drop it: a later PIN edit must wait for a new OAuth flow.
      return { ...state, oauthError: action.error, isConnecting: false, oauthCode: undefined };
    case 'CLEAR_ERROR':
      return { ...state, oauthError: undefined };
    case 'SET_CONNECTING':
      return { ...state, isConnecting: action.isConnecting };
    case 'SET_CONFLICT':
      // Drop oauthCode/wabaId so the completion effect won't re-fire against the same conflict.
      return {
        ...state,
        conflict: action.conflict,
        oauthCode: undefined,
        wabaId: undefined,
        phoneNumberId: undefined,
        oauthEvent: undefined,
        isConnecting: false,
      };
    case 'RESET':
      return oauthInitialState;
    default:
      return state;
  }
};

export const endpoints = {
  getData: (slug: string, deskId: string, integrationId: string) =>
    `/www/api/integrations/whatsapp/${slug}/desks/${deskId}/integrations/${integrationId}/whatsapp`,
};

export const whatsappQueryKeys = {
  all: ['whatsapp'] as const,
  data: (slug: string, deskId: string, integrationId: string) =>
    [...whatsappQueryKeys.all, 'data', { slug, deskId, integrationId }] as const,
};

export const invalidateWhatsappQueries = (
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  slug: string,
  deskId: string,
  integrationId: string
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: [integrationsEndpoints.integration(deskId, integrationId)],
    }),
    queryClient.invalidateQueries({ queryKey: [integrationsEndpoints.integrations(deskId)] }),
    queryClient.invalidateQueries({
      queryKey: whatsappQueryKeys.data(slug, deskId, integrationId),
    }),
  ]);

export const useWhatsapp = () => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const slug = useSelector(selectAccountSlug);
  const queryClient = useQueryClient();

  const [oauthState, dispatch] = useReducer(oauthReducer, oauthInitialState);
  const [pin, setPin] = useState('');
  const pinRef = useRef(pin);
  useEffect(() => {
    pinRef.current = pin;
  }, [pin]);
  const { phoneNumberId, wabaId, oauthCode, oauthError, oauthEvent, isConnecting, conflict } =
    oauthState;

  // Phone change confirmation state
  const [phoneChangeInfo, setPhoneChangeInfo] = useState<{
    previousPhone: string;
    newPhone: string;
  } | null>(null);

  const { integration } = useIntegrations<WhatsAppIntegration>(deskId, integrationId);

  const config = integration?.config;
  const isConfigured = Boolean(config?.access_token && config?.phone_number_id && config?.page_id);

  // Use ref to avoid re-triggering OAuth completion effect when isConfigured changes
  const isConfiguredRef = useRef(isConfigured);
  useEffect(() => {
    isConfiguredRef.current = isConfigured;
  }, [isConfigured]);

  // Meta emits several intermediate events per number selection; preflight each number only once.
  const lastPreflightedRef = useRef<string | undefined>(undefined);

  const {
    data,
    isLoading,
    error: dataError,
  } = useQuery<WhatsappData>({
    queryKey: whatsappQueryKeys.data(slug, deskId, integrationId),
    queryFn: () => callGet(endpoints.getData(slug, deskId, integrationId)),
    enabled: isConfigured,
  });

  // Maps a preflight/callback error to the inline conflict UI. Returns true if it was a conflict.
  const dispatchConflictFromError = useCallback(
    (error: {
      code?: string;
      owner?: { account_slug: string; integration_id: string; desk_id: string };
    }): boolean => {
      if (error?.code === WHATSAPP_CONFLICT_CODES.WITH_ACCESS && error?.owner) {
        dispatch({
          type: 'SET_CONFLICT',
          conflict: {
            kind: 'with-access',
            slug: error.owner.account_slug,
            integrationId: error.owner.integration_id,
            deskId: error.owner.desk_id,
          },
        });
        return true;
      }
      if (error?.code === WHATSAPP_CONFLICT_CODES.NO_ACCESS) {
        dispatch({ type: 'SET_CONFLICT', conflict: { kind: 'no-access' } });
        return true;
      }
      return false;
    },
    []
  );

  // Preflight the number Meta reports at selection, before Finish, on fresh installs. Reauth is
  // locked to the caller's own number (server self-excludes by integration_id), so skip it.
  const preflightSelectedNumber = useCallback(
    async (selectedPhoneNumberId: string) => {
      if (isConfiguredRef.current || lastPreflightedRef.current === selectedPhoneNumberId) {
        return;
      }
      lastPreflightedRef.current = selectedPhoneNumberId;
      try {
        await callPost('/www/api/integrations/whatsapp/preflight', {
          phone_number_id: selectedPhoneNumberId,
          integration_id: integrationId,
          desk_id: deskId,
          slug,
        });
      } catch (error) {
        // Non-conflict errors stay silent; /callback preflight is the backstop on Finish.
        dispatchConflictFromError(error);
      }
    },
    [deskId, dispatchConflictFromError, integrationId, slug]
  );

  const onOAuthClick = useCallback(() => {
    lastPreflightedRef.current = undefined;
    // Reauth: pre-select the existing WABA/number so the user stays locked to what they own.
    // Match the server getWabaId fallback (waba_id, else legacy page_id).
    if (isConfiguredRef.current) {
      const existingWabaId = config?.waba_id ?? config?.page_id;
      const existingPhoneNumberId = config?.phone_number_id ?? config?.page_id;
      const featureType = config?.is_coexistence ? 'coexistence' : 'standard';
      performWhatsappOAuth(dispatch, t, featureType, existingWabaId, existingPhoneNumberId);
      return;
    }
    performWhatsappOAuth(dispatch, t, 'coexistence', undefined, undefined, preflightSelectedNumber);
  }, [
    config?.is_coexistence,
    config?.page_id,
    config?.phone_number_id,
    config?.waba_id,
    preflightSelectedNumber,
    t,
  ]);

  useEffect(() => {
    // Load Facebook SDK asynchronously
    initializeFacebookSdk();
  }, []);

  const completeOAuth = useCallback(
    async (confirmPhoneChange = false) => {
      if (!oauthCode || !wabaId) {
        return;
      }

      dispatch({ type: 'SET_CONNECTING', isConnecting: true });
      try {
        const endpoint = isConfiguredRef.current ? 'reauthorise' : 'callback';
        const response = await callPost(`/www/api/integrations/whatsapp/${endpoint}`, {
          code: oauthCode,
          waba_id: wabaId,
          phone_number_id: phoneNumberId,
          integration_id: integrationId,
          desk_id: deskId,
          slug,
          oauth_event: oauthEvent,
          confirm_phone_change: confirmPhoneChange,
          pin: pinRef.current || undefined,
        });

        // Handle phone number change requiring confirmation
        if (response?.code === 'phone-number-changed') {
          setPhoneChangeInfo({
            previousPhone: response.previous_phone,
            newPhone: response.new_phone,
          });
          dispatch({ type: 'SET_CONNECTING', isConnecting: false });
          return;
        }

        await invalidateWhatsappQueries(queryClient, slug, deskId, integrationId);
        dispatch({ type: 'RESET' });
      } catch (error) {
        // Conflicts surface inline on the OAuthCard (not-connected view) as persistent UI, since
        // re-clicking Connect can't resolve them. Reauth (connected view) has no inline surface,
        // so its conflicts fall through to a toast below.
        if (!isConfiguredRef.current && dispatchConflictFromError(error)) {
          return;
        }
        const errorMessages: Record<string, string> = {
          'update-integration': t('errors.whatsapp_duplicated'),
          'phone-number-ambiguous': t('errors.whatsapp_phone_number_ambiguous'),
          'phone-change-expired': t('errors.whatsapp_phone_change_expired'),
          'invalid-pin': t('errors.whatsapp_invalid_pin'),
          'pin-mismatch': t('errors.whatsapp_pin_mismatch'),
          'registration-blocked': t('errors.whatsapp_registration_blocked'),
          'reverification-required': t('broadcasts.errors.phone_number_re_verification_needed'),
          [WHATSAPP_CONFLICT_CODES.WITH_ACCESS]: t('errors.whatsapp_already_attached_with_access'),
          [WHATSAPP_CONFLICT_CODES.NO_ACCESS]: t('errors.whatsapp_already_attached_no_access'),
        };
        dispatch({
          type: 'SET_OAUTH_ERROR',
          error: errorMessages[error?.code] || error?.message || t('errors.generic'),
        });
      } finally {
        dispatch({ type: 'SET_CONNECTING', isConnecting: false });
      }
    },
    [
      deskId,
      dispatchConflictFromError,
      integrationId,
      oauthCode,
      oauthEvent,
      phoneNumberId,
      queryClient,
      slug,
      t,
      wabaId,
    ]
  );

  useEffect(() => {
    // Only proceed after a completed WhatsApp OAuth flow
    // For coexistence flow, phoneNumberId may not be available - server fetches it from Meta API
    if (!oauthCode || !wabaId) {
      return;
    }

    completeOAuth();
  }, [oauthCode, wabaId, completeOAuth]);

  useEffect(() => {
    if (oauthError) {
      toast.error(oauthError);
      dispatch({ type: 'CLEAR_ERROR' });
    }
  }, [oauthError]);

  const onConfirmPhoneChange = useCallback(() => {
    setPhoneChangeInfo(null);
    completeOAuth(true);
  }, [completeOAuth]);

  const onCancelPhoneChange = useCallback(() => {
    setPhoneChangeInfo(null);
    dispatch({ type: 'RESET' });
  }, []);

  const uri = useMemo(() => {
    const phone = data?.display_phone_number?.replace(/(\+| |\(|\)|-)/g, '');
    if (!phone) {
      return null;
    }

    const baseUrl = `https://wa.me/${phone}`;
    return config?.prefilled_message
      ? `${baseUrl}?text=${encodeURIComponent(config.prefilled_message)}`
      : baseUrl;
  }, [config?.prefilled_message, data?.display_phone_number]);

  return {
    uri,
    data,
    isConfigured,
    isLoading,
    error: !isLoading && !data?.display_phone_number,
    dataError,
    integration,
    connectionStatus: isConfigured ? 'connected' : 'not-connected',
    onOAuthClick,
    isConnecting,
    conflict,
    pin,
    setPin,
    // Phone change confirmation
    phoneChangeInfo,
    onConfirmPhoneChange,
    onCancelPhoneChange,
  };
};
